import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import { cn } from '../lib/utils';

interface Props {
  value: string; // numeric player ID as string
  onChange(playerId: string, name?: string): void;
}

// Typeahead-backed picker for the WC Golden Boot prediction. Pulls candidates
// from /api/wc/players/search which reads the live worker's KV cache of
// API-Football top scorers. Pre-WC the cache is empty — we degrade to a plain
// player-ID input.
export function GoalscorerSearch({ value, onChange }: Props) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedName, setSelectedName] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  const search = useQuery({
    queryKey: ['wc', 'players', 'search', debounced],
    queryFn: () => api.wcPlayerSearch(debounced),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });

  const pending = !!search.data?.pending;

  if (pending) {
    return (
      <div className="space-y-2">
        <div className="text-xs text-text-dim">
          Player search index is empty until the tournament kicks off. Paste a player ID for now —
          you can swap to a name pick once matches start.
        </div>
        <input
          type="number"
          placeholder="Player ID"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-trophy-gold"
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          placeholder="Search a player (e.g. Mbappé)"
          value={selectedName ?? q}
          onChange={(e) => {
            setSelectedName(null);
            setQ(e.target.value);
            setOpenMenu(true);
          }}
          onFocus={() => setOpenMenu(true)}
          onBlur={() => setTimeout(() => setOpenMenu(false), 150)}
          className="w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-trophy-gold"
        />
        {openMenu && search.data && search.data.players.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-lg border border-border-base bg-bg-elevated shadow-lg max-h-64 overflow-y-auto">
            {search.data.players.map((p) => (
              <button
                key={p.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  haptic.select();
                  onChange(String(p.id), p.name);
                  setSelectedName(p.name);
                  setOpenMenu(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 hover:bg-bg-card flex items-center justify-between',
                  String(p.id) === value && 'bg-trophy-gold/10',
                )}
              >
                <span className="font-chunky text-sm">{p.name}</span>
                <span className="font-mono text-[10px] text-text-faint">{p.teamName ?? '—'}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedName && (
        <div className="text-xs text-trophy-gold">
          Picked: {selectedName} <span className="font-mono text-text-faint">#{value}</span>
        </div>
      )}
    </div>
  );
}
